import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import API_URL, { getAuthHeaders } from '../utils/api';

function PreciosUnitarios() {
  const [preciosUnitarios, setPreciosUnitarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    fetchPreciosUnitarios();
  }, [search]);

  const fetchPreciosUnitarios = async () => {
    try {
      setLoading(true);
      const url = search
        ? `${API_URL}/precios-unitarios?search=${encodeURIComponent(search)}`
        : `${API_URL}/precios-unitarios`;

      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Error al cargar precios unitarios');

      const data = await response.json();
      setPreciosUnitarios(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    });
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Precios Unitarios</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Nuevo Precio Unitario
        </Button>
      </Box>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Análisis de precios unitarios con desglose completo de costos directos e indirectos
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Buscar por código o descripción"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Unidad</TableCell>
                <TableCell align="right">Costo Directo</TableCell>
                <TableCell align="right">Indirectos</TableCell>
                <TableCell align="right">Financiamiento</TableCell>
                <TableCell align="right">Utilidad</TableCell>
                <TableCell align="right">Precio Unitario</TableCell>
                <TableCell align="center">Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preciosUnitarios.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    {search
                      ? 'No se encontraron precios unitarios con ese criterio'
                      : 'No hay precios unitarios registrados. Crea conceptos desde el módulo de presupuestos para generar PU automáticamente.'
                    }
                  </TableCell>
                </TableRow>
              ) : (
                preciosUnitarios.map((pu) => (
                  <TableRow key={pu.id}>
                    <TableCell>{pu.codigo}</TableCell>
                    <TableCell>{pu.descripcion}</TableCell>
                    <TableCell>{pu.unidades?.codigo}</TableCell>
                    <TableCell align="right">{formatCurrency(pu.costo_directo)}</TableCell>
                    <TableCell align="right">{formatCurrency(pu.costo_indirecto)}</TableCell>
                    <TableCell align="right">{formatCurrency(pu.costo_financiamiento)}</TableCell>
                    <TableCell align="right">{formatCurrency(pu.costo_utilidad)}</TableCell>
                    <TableCell align="right">{formatCurrency(pu.precio_unitario)}</TableCell>
                    <TableCell align="center">
                      <span style={{ color: pu.activo ? '#388e3c' : '#d32f2f' }}>
                        {pu.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Crear Nuevo Precio Unitario</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Los precios unitarios se generan automáticamente cuando creas conceptos en un presupuesto.
            Para crear PU manualmente, utiliza el módulo de análisis de precios unitarios en el editor de presupuestos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default PreciosUnitarios;